<template>
  <v-card outlined class="rounded-xl overflow-hidden">
    <v-card-title class="pa-5 pa-sm-6 align-start">
      <v-avatar color="primary" size="42" class="mr-4">
        <v-icon dark>mdi-email-plus-outline</v-icon>
      </v-avatar>
      <div>
        <div class="headline">Invitations</div>
        <div class="body-2 text--secondary mt-1">
          Invite someone to access this organization and track pending invitations.
        </div>
      </div>
    </v-card-title>

    <v-divider />

    <v-card-text v-if="canManageAccess === true" class="pa-4 pa-sm-6">
      <v-form v-if="!hideCreate" @submit.prevent="sendInvite">
        <v-row align="start" dense>
          <v-col cols="12" sm="8" md="6">
            <v-text-field
              v-model.trim="inviteEmail"
              label="Email address"
              prepend-inner-icon="mdi-email-outline"
              :error-messages="inviteEmailErrors"
              clearable dense outlined
              autocomplete="email"
              hint="Existing users receive access immediately. New users receive access after completing signup."
              persistent-hint
              @blur="$v.inviteEmail.$touch()"
            />
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-btn type="submit" color="primary" block :loading="sending" class="mt-sm-0">
              <v-icon left>mdi-send</v-icon>Send invitation
            </v-btn>
          </v-col>
        </v-row>
      </v-form>

      <v-alert v-if="error" type="error" text dense class="mt-4 mb-0">
        <div class="d-flex align-center">
          <span>{{ error }}</span><v-spacer />
          <v-btn text small color="error" @click="getList">Retry</v-btn>
        </div>
      </v-alert>

      <div class="subtitle-1 font-weight-medium mt-6 mb-3">Pending invitations</div>
      <v-data-table
        :headers="headers"
        :items="invites"
        :options.sync="pagination"
        :server-items-length="count"
        :loading="loading"
        :items-per-page="pagination.itemsPerPage"
        class="invite-list__table"
        mobile-breakpoint="720"
      >
        <template v-slot:item.createdAt="{ item }">
          {{ item.createdAt ? $moment(item.createdAt).format("MMM D, YYYY") : "Not available" }}
        </template>
        <template v-slot:item.to="{ item }">
          <span class="font-weight-medium">{{ item.to }}</span>
        </template>
        <template v-slot:item.status>
          <v-chip small color="warning" outlined>
            <v-icon left small>mdi-clock-outline</v-icon>Pending
          </v-chip>
        </template>
        <template v-slot:item.action="{ item }">
          <v-btn text small color="error" :loading="revokingId === item.id" @click="revokeInvite(item)">
            Revoke
          </v-btn>
        </template>
        <template v-slot:no-data>
          <div class="py-8 text-center">
            <v-icon size="40" color="grey lighten-1">mdi-email-check-outline</v-icon>
            <div class="subtitle-1 mt-2">No pending invitations</div>
            <div class="body-2 text--secondary mt-1">New invitations will appear here until accepted or revoked.</div>
          </div>
        </template>
      </v-data-table>
    </v-card-text>
    <v-card-text v-else-if="canManageAccess === false" class="pa-5 pa-sm-6">
      <v-alert text type="info" class="mb-0">
        Only the organization owner can invite users or manage pending invitations.
      </v-alert>
    </v-card-text>
    <v-card-text v-else class="pa-5 pa-sm-6">
      <v-skeleton-loader type="text" />
    </v-card-text>
  </v-card>
</template>

<script>
import userService from "../service";
import { validationMixin } from "vuelidate";
import { required, email } from "vuelidate/lib/validators";

export default {
  name: "user-invite",
  mixins: [validationMixin],
  props: {
    hideCreate: Boolean,
    canManageAccess: { type: Boolean, default: null },
  },
  data() {
    return {
      headers: [
        { text: "Sent", value: "createdAt", sortable: true },
        { text: "Email address", value: "to", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "", value: "action", sortable: false, align: "end" },
      ],
      inviteEmail: "",
      invites: [], count: 0, loading: false, sending: false, revokingId: null, error: "",
      pagination: { page: 1, itemsPerPage: 10, sortBy: ["createdAt"], sortDesc: [true] },
    };
  },
  computed: {
    inviteEmailErrors() {
      if (!this.$v.inviteEmail.$dirty) return [];
      if (!this.$v.inviteEmail.required) return ["Email address is required"];
      return this.$v.inviteEmail.email ? [] : ["Enter a valid email address"];
    },
  },
  watch: {
    canManageAccess: {
      immediate: true,
      handler(value) { if (value === true) this.getList(); },
    },
    pagination: {
      handler() { if (this.canManageAccess === true) this.getList(); },
      deep: true,
    },
  },
  methods: {
    async sendInvite() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      this.sending = true;
      try {
        await userService.sendInvite({ email: this.inviteEmail });
        this.inviteEmail = "";
        this.$v.$reset();
        this.$toasted.success("Invitation sent.", { position: "top-right", duration: 3500 });
        await this.getList();
      } catch (error) {
        this.$toasted.error(this.errorMessage(error, "Unable to send invitation."), { position: "top-right", duration: 3500 });
      } finally { this.sending = false; }
    },
    async getList() {
      this.loading = true; this.error = "";
      const { page, itemsPerPage, sortBy, sortDesc } = this.pagination;
      try {
        const response = await userService.getInvites({
          limit: itemsPerPage, offset: itemsPerPage * (page - 1),
          orderBy: [sortBy[0] || "createdAt", Boolean(sortDesc[0])], status: "pending",
        });
        this.invites = response.data || []; this.count = response.count || 0;
      } catch (error) { this.error = this.errorMessage(error, "Unable to load invitations."); }
      finally { this.loading = false; }
    },
    async revokeInvite(invite) {
      const result = await this.$swal.fire({
        title: "Revoke invitation?",
        text: `${invite.to} will no longer be able to accept this invitation.`,
        type: "warning", showCancelButton: true, confirmButtonText: "Revoke invitation",
        cancelButtonText: "Cancel", confirmButtonColor: this.$vuetify.theme.currentTheme.error,
      });
      if (!result.value) return;
      this.revokingId = invite.id;
      try {
        await userService.revokeInvite(invite.id);
        this.$toasted.success("Invitation revoked.", { position: "top-right", duration: 3500 });
        await this.getList();
      } catch (error) {
        this.$toasted.error(this.errorMessage(error, "Unable to revoke invitation."), { position: "top-right", duration: 3500 });
      } finally { this.revokingId = null; }
    },
    errorMessage(error, fallback) {
      return (error && error.data && (error.data.message || error.data)) ||
        (error && error.response && error.response.data && (error.response.data.message || error.response.data)) || fallback;
    },
  },
  validations: { inviteEmail: { required, email } },
};
</script>

<style scoped>
.invite-list__table { border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 12px; overflow: hidden; }
.theme--dark .invite-list__table { border-color: rgba(255, 255, 255, 0.12); }
</style>
